"use client";

import { useToast } from "@/components/providers/FetchToastProvider";
import { FetchPill } from "@/fetch-ui/FetchPill";
import type { FormattedLocation } from "@/hooks/useLocationSearch";
import { Check, MapPin, Plus, X } from "lucide-react";
import { useMemo, useRef } from "react";
import { resolveCityToLocation } from "@/lib/events/location-resolution";
import { buildOptimisticLocation } from "../../_logic/location-utils";
import { SUGGESTED_LOCATIONS, SuggestedLocation } from "../../_logic/constants";
import { CreateEventFormData, WizardAction } from "../../_types/wizard-types";

interface SuggestedLocationsProps {
  formData: CreateEventFormData;
  dispatch: React.Dispatch<WizardAction>;
  /** Max number of locations the step allows (matches the autocomplete's limit). */
  limit: number;
}

const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * A row of one-click suggestion pills (curated top cities) for the location step. Clicking a pill
 * toggles its city into the multiselect. Adds are **optimistic**: the selection appears instantly
 * (placeholder), the real `FormattedLocation` resolves via Google Places in the background, then the
 * `RESOLVE_LOCATION` reducer swaps it in (or rolls back + toasts on failure). Re-adding a
 * previously-resolved city is instant from cache. Active state and removal key off the resolved
 * `place_id`/address (via `locationsMatch`) — the same identity the reducer dedupes on — so the
 * highlight can't desync or add a duplicate.
 *
 * @param props - The wizard form data, dispatch, and selection limit.
 * @returns The suggestions row.
 */
export const SuggestedLocations = ({ formData, dispatch, limit }: SuggestedLocationsProps) => {
  // Hooks
  const { showToast } = useToast();

  // States
  // Resolved location per suggestion, so active-state and re-adds use the reducer's place_id/address
  // identity (and skip a repeat network call). A ref — read on the re-render the dispatch triggers.
  const resolvedCache = useRef(new Map<string, FormattedLocation>());

  // Derived values
  const atLimit = formData.formattedLocations.length >= limit;
  // O(1) lookups for findSelected, keyed the same way locationsMatch compares (place_id, falling back to
  // formatted_address) and by normalized city name for the optimistic/best-effort path.
  const locationsByIdentity = useMemo(() => {
    const map = new Map<string, FormattedLocation>();
    for (const loc of formData.formattedLocations) {
      map.set(loc.place_id || normalize(loc.formatted_address), loc);
    }
    return map;
  }, [formData.formattedLocations]);
  const locationsByCity = useMemo(() => {
    const map = new Map<string, FormattedLocation>();
    for (const loc of formData.formattedLocations) {
      map.set(normalize(loc.city), loc);
    }
    return map;
  }, [formData.formattedLocations]);
  // The selected location for a suggestion: by resolved place_id/address once cached, else best-effort
  // by city name (covers optimistic placeholders, autocomplete-typed, and Northstar-prefilled cities).
  const findSelected = (suggestion: SuggestedLocation): FormattedLocation | undefined => {
    const cached = resolvedCache.current.get(suggestion.query);
    if (cached) return locationsByIdentity.get(cached.place_id || normalize(cached.formatted_address));
    return locationsByCity.get(normalize(suggestion.label));
  };

  // Handlers
  const handleToggle = async (suggestion: SuggestedLocation) => {
    // Already selected (or mid-resolve) → remove the existing entry directly (reducer's identity).
    const existing = findSelected(suggestion);
    if (existing) {
      dispatch({ type: "TOGGLE_LOCATION", payload: { location: existing, limit } });
      return;
    }

    if (atLimit) {
      showToast(`You can select up to ${limit} locations.`, "warning");
      return;
    }

    // Previously resolved → add the real location instantly, no network round-trip.
    const cached = resolvedCache.current.get(suggestion.query);
    if (cached) {
      dispatch({ type: "TOGGLE_LOCATION", payload: { location: cached, limit } });
      return;
    }

    // Optimistic: show the selection immediately, resolve in the background, then reconcile.
    const optimistic = buildOptimisticLocation(suggestion.label, suggestion.query);
    dispatch({ type: "TOGGLE_LOCATION", payload: { location: optimistic, limit } });

    const resolved = await resolveCityToLocation(suggestion.query);
    if (resolved) resolvedCache.current.set(suggestion.query, resolved);
    dispatch({ type: "RESOLVE_LOCATION", payload: { placeId: optimistic.place_id, location: resolved } });
    if (!resolved) showToast("Couldn't add that location. Try searching for it.", "error");
  };

  // Render
  return (
    <div className="mt-5 flex flex-col gap-2.5">
      <span id="suggested-locations-label" className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
        <MapPin className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
        Popular cities
      </span>
      <div role="group" aria-labelledby="suggested-locations-label" className="flex flex-wrap gap-2">
        {SUGGESTED_LOCATIONS.map((suggestion) => {
          const active = !!findSelected(suggestion);
          const limitBlocked = atLimit && !active;
          return (
            <FetchPill
              key={suggestion.query}
              type="button"
              size="sm"
              selected={active}
              aria-disabled={limitBlocked}
              onClick={() => handleToggle(suggestion)}
              className={`group gap-1.5 px-3 py-1 font-medium transition-[background-color,transform] duration-150 active:scale-[0.97] motion-reduce:transition-colors motion-reduce:active:scale-100 ${
                limitBlocked ? "opacity-50" : ""
              }`}
            >
              {active ? (
                <>
                  {/* Selected shows a check; hovering reveals an × to signal "click to remove". */}
                  <Check className="-ml-0.5 h-3.5 w-3.5 group-hover:hidden" aria-hidden="true" />
                  <X className="-ml-0.5 hidden h-3.5 w-3.5 group-hover:block" aria-hidden="true" />
                </>
              ) : (
                <Plus className="-ml-0.5 h-3.5 w-3.5 opacity-60" aria-hidden="true" />
              )}
              {suggestion.label}
            </FetchPill>
          );
        })}
      </div>
    </div>
  );
};
